import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { applyContentLocaleToUrl } from '../../utils/contentLocaleQuery';

type AppFooterProps = {
  /**
   * When true (customer account + fixed `lg:w-52` rail), footer content starts after the rail
   * and uses the remaining width — full-bleed bar, no empty strip under the sidebar.
   */
  accountChromeOffset?: boolean;
};

function AppFooter({ accountChromeOffset = false }: AppFooterProps) {
  const { t, i18n } = useTranslation();
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
  const [publicContact, setPublicContact] = React.useState({
    address: '123 Street, New York, USA',
    email: 'info@example.com',
    phone: '+1 (555) 012-9012',
  });
  const [socialLinks, setSocialLinks] = React.useState({
    socialFacebookUrl: 'https://facebook.com',
    socialInstagramUrl: 'https://instagram.com',
    socialLinkedinUrl: 'https://linkedin.com',
    socialXUrl: 'https://x.com',
  });
  const supportedPayments = React.useMemo(
    () => [
      {
        label: 'Amazon Pay',
        src: 'https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/apm/amazon-pay.svg?sanitize=true',
      },
      {
        label: 'American Express',
        src: 'https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/cards/american-express.svg?sanitize=true',
      },
      {
        label: 'Apple Pay',
        src: 'https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/wallets/apple-pay.svg?sanitize=true',
      },
      {
        label: 'Bancontact',
        src: 'https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/apm/bancontact.svg?sanitize=true',
      },
      {
        label: 'Diners',
        src: 'https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/cards/diners.svg?sanitize=true',
      },
      {
        label: 'Discover',
        src: 'https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/cards/discover.svg?sanitize=true',
      },
      {
        label: 'Google Pay',
        src: 'https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/wallets/google-pay.svg?sanitize=true',
      },
      {
        label: 'iDEAL',
        src: 'https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/apm/ideal.svg?sanitize=true',
      },
      {
        label: 'Wero',
        src: 'https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/apm/wero.svg?sanitize=true',
      },
      {
        label: 'Mastercard',
        src: 'https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/cards/mastercard.svg?sanitize=true',
      },
      {
        label: 'PayPal',
        src: 'https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/apm/paypal.svg?sanitize=true',
      },
      {
        label: 'Visa',
        src: 'https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/cards/visa.svg?sanitize=true',
      },
    ],
    [],
  );

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          applyContentLocaleToUrl(
            `${API_BASE}/settings/public-contact`,
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok) return;
        const data = (await res.json()) as
          | { address?: string; email?: string; phone?: string }
          | null;
        if (cancelled || !data) return;
        setPublicContact((prev) => ({
          address: data.address ? String(data.address) : prev.address,
          email: data.email ? String(data.email) : prev.email,
          phone: data.phone ? String(data.phone) : prev.phone,
        }));
      } catch {
        // keep defaults
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, i18n.language, i18n.resolvedLanguage]);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          applyContentLocaleToUrl(
            `${API_BASE}/public/site-content`,
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok) return;
        const data = (await res.json()) as {
          socialFacebookUrl?: string;
          socialInstagramUrl?: string;
          socialLinkedinUrl?: string;
          socialXUrl?: string;
        };
        if (cancelled) return;
        setSocialLinks((prev) => ({
          socialFacebookUrl: data.socialFacebookUrl?.trim() || prev.socialFacebookUrl,
          socialInstagramUrl: data.socialInstagramUrl?.trim() || prev.socialInstagramUrl,
          socialLinkedinUrl: data.socialLinkedinUrl?.trim() || prev.socialLinkedinUrl,
          socialXUrl: data.socialXUrl?.trim() || prev.socialXUrl,
        }));
      } catch {
        // keep defaults
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, i18n.language, i18n.resolvedLanguage]);

  const mainGridClass = [
    'grid gap-8 px-4 py-10 sm:px-6 lg:grid-cols-3 lg:px-8',
    accountChromeOffset
      ? 'lg:mx-0 lg:max-w-none lg:pl-[calc(13rem+2rem)] lg:pr-8'
      : 'mx-auto max-w-6xl',
  ].join(' ');

  const bottomBarInnerClass = [
    'flex max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-4 text-xs text-slate-400 sm:px-6 lg:px-8',
    accountChromeOffset
      ? 'lg:mx-0 lg:max-w-none lg:w-full lg:pl-[calc(13rem+2rem)] lg:pr-8'
      : 'mx-auto',
  ].join(' ');

  return (
    <footer className="w-full border-t border-slate-800 bg-slate-900 text-slate-200">
      <div className={mainGridClass}>
        <div>
          <h3 className="text-lg font-extrabold tracking-tight">
            <span style={{ color: 'var(--storefront-brand-primary, #059669)' }}>Lewouh</span>
            <span style={{ color: 'var(--storefront-nav-accent, #ea580c)' }}>mart</span>
          </h3>
          <p className="mt-3 text-xs leading-relaxed text-slate-300">
            {t('footer.storefront_about')}
          </p>
          <p className="mt-3 text-xs text-slate-400">{t('footer.footer_right')}</p>
          <div className="mt-4 flex items-center gap-3 text-sm text-slate-300">
            <a href={socialLinks.socialFacebookUrl} target="_blank" rel="noreferrer" className="hover:text-white" aria-label={t('social.facebook', { defaultValue: 'Facebook' })}><i className="fab fa-facebook-f" /></a>
            <a href={socialLinks.socialInstagramUrl} target="_blank" rel="noreferrer" className="hover:text-white" aria-label={t('social.instagram', { defaultValue: 'Instagram' })}><i className="fab fa-instagram" /></a>
            <a href={socialLinks.socialLinkedinUrl} target="_blank" rel="noreferrer" className="hover:text-white" aria-label={t('social.linkedin', { defaultValue: 'LinkedIn' })}><i className="fab fa-linkedin-in" /></a>
            <a href={socialLinks.socialXUrl} target="_blank" rel="noreferrer" className="hover:text-white" aria-label={t('social.x', { defaultValue: 'X' })}><i className="fab fa-twitter" /></a>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-white">{t('footer.customer_heading')}</h4>
          <div className="mt-3 space-y-2 text-xs">
            <Link to="/" className="block hover:text-white">{t('footer.link_home')}</Link>
            <Link to="/products" className="block hover:text-white">{t('footer.link_products')}</Link>
            <Link to="/cart" className="block hover:text-white">{t('footer.link_cart')}</Link>
            <Link to="/account/orders" className="block hover:text-white">{t('footer.link_track_orders')}</Link>
            <Link to="/feedback" className="block hover:text-white">{t('footer.link_feedback_complaints')}</Link>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-white">{t('footer.support_heading')}</h4>
          <div className="mt-3 space-y-2 text-xs">
            <span className="block text-slate-300">{publicContact.email}</span>
            <span className="block text-slate-300">{publicContact.phone}</span>
            <Link to="/about" className="block text-slate-300 hover:text-white">{t('footer.link_about')}</Link>
            <Link to="/legal" className="block text-slate-300 hover:text-white">{t('footer.terms')}</Link>
            <Link to="/legal" className="block text-slate-300 hover:text-white">{t('footer.privacy')}</Link>
            <Link to="/cookie-settings" className="block text-slate-300 hover:text-white">{t('footer.link_cookie_settings')}</Link>
            <span className="block text-slate-300">{publicContact.address}</span>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div
          className={[
            'mx-auto w-full max-w-6xl px-4 pt-5 sm:px-6 lg:px-8',
            accountChromeOffset
              ? 'lg:mx-0 lg:max-w-none lg:w-full lg:pl-[calc(13rem+2rem)] lg:pr-8'
              : '',
          ].join(' ')}
        >
          <p className="text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {t('footer.supported_payments', { defaultValue: 'Supported payments' })}
          </p>
          <div className="mt-3 flex w-full flex-wrap items-center justify-center gap-2 pb-4">
            {supportedPayments.map((method) => (
              <span
                key={method.label}
                className="inline-flex h-8 items-center rounded-md border border-slate-200 bg-white px-2 shadow-sm"
                title={method.label}
              >
                <img
                  src={method.src}
                  alt={method.label}
                  loading="lazy"
                  className="h-4 w-auto max-w-[70px] object-contain"
                />
              </span>
            ))}
          </div>
        </div>
        <div className={bottomBarInnerClass}>
          <span>
            © {new Date().getFullYear()} {t('layout.app_name')}. {t('footer.copyright')}
          </span>
          <span className="hidden sm:inline">{t('footer.storefront_tagline')}</span>
        </div>
      </div>
    </footer>
  );
}

export default AppFooter;
